import * as React from 'react'
import styled, { AnyStyledComponent } from 'styled-components'
import I18n from '../../../../core/i18n'
import { IJsonResponse } from '../../../../core/JsonApiSerializer'
import { agentService, utilService } from '../../../../core/services'
import { BREAKPOINT_TABLET_SMALL } from '../../../../static/constants'
import * as constants from '../../../../static/constants'
import * as interfaces from '../../../../static/interfaces'
import { Button, InputText, InputTextArea, Panel, Toggle } from '../../../atoms'
import { Form } from '../../../molecules'
import SidebarLayout from '../../_layouts/sidebarLayout'
import SvgIcon from 'components/atoms/SvgIcon'

declare var window: interfaces.IWindow

const FIELDS = {
  name: 'name',
  activity_title: 'activity_title',
  activity_body: 'activity_body',
  activity_link_type: 'activity_link_type',
  email_subject: 'email_subject',
  email_body: 'email_body',
  mobile_push_data: 'mobile_push_data',
  short_limit: 'short_limit',
}

const title = I18n.t('admin.edit', {
  model: I18n.t('guide_notification_type', { scope: 'activerecord.models' }),
})

interface IProps {
  guide_notification_type: IJsonResponse
  user: IJsonResponse
}

const AgentGuideNotificationTypeEdit: React.FC<IProps> = props => {
  const { data: userData } = utilService.getDataFromJson(props.user)
  const { data: notificationType } = agentService.getDataFromJson(props.guide_notification_type)

  const [isActive, setIsActive] = React.useState(notificationType.is_active)
  const [isActivity, setIsActivity] = React.useState(notificationType.is_activity)
  const [isEmail, setIsEmail] = React.useState(notificationType.is_email)
  const [isMobilePush, setIsMobilePush] = React.useState(notificationType.is_mobile_push)

  const handleSubmit = React.useCallback(
    async (initialValues, values) => {
      const result = { ...values }
      result.is_active = isActive
      result.is_activity = isActivity
      result.is_email = isEmail
      result.is_mobile_push = isMobilePush
      const { flush } = await agentService.updateGuideNotificationType(notificationType.id, result)
      window.flashMessages.addMessage({ text: flush.message, type: flush.type })
      location.href = '/agent/guide_notification_types'
    },
    [isActive, isActivity, isEmail, isMobilePush]
  )

  return (
    <SidebarLayout user={userData}>
      <Wrapper>
        <Panel title={title}>
          <Form fields={FIELDS} handleSubmit={handleSubmit}>
            <InputText
              required={true}
              name="name"
              defaultValue={notificationType.name || ''}
              label={I18n.t('generic.name')}
            />
            <S.FormItemWrapper>
              <InputText
                required={false}
                name="activity_title"
                defaultValue={notificationType.activity_title || ''}
                label="アプリ内 タイトル"
              />
            </S.FormItemWrapper>
            <S.FormItemWrapper>
              <InputTextArea
                required={false}
                name="activity_body"
                defaultValue={notificationType.activity_body || ''}
                label="アプリ内 本文"
              />
              <p className="note">テンプレート変数は [[変数名]] の形式で使用できます</p>
            </S.FormItemWrapper>
            <S.FormItemWrapper>
              <InputText
                required={false}
                name="activity_link_type"
                defaultValue={notificationType.activity_link_type || ''}
                label="アプリ内 リンクタイプ"
              />
            </S.FormItemWrapper>
            <S.FormItemWrapper>
              <InputText
                required={false}
                name="email_subject"
                defaultValue={notificationType.email_subject || ''}
                label="メール件名"
              />
            </S.FormItemWrapper>
            <S.FormItemWrapper>
              <InputTextArea
                required={false}
                name="email_body"
                defaultValue={notificationType.email_body || ''}
                label="メール本文"
              />
            </S.FormItemWrapper>
            <S.FormItemWrapper>
              <InputTextArea
                required={false}
                name="mobile_push_data"
                defaultValue={notificationType.mobile_push_data || ''}
                label="プッシュ通知データ"
              />
            </S.FormItemWrapper>
            <S.FormItemWrapper>
              <Toggle
                name="is_active"
                checked={isActive}
                onChangeHandler={() => setIsActive(!isActive)}
                label="Active"
              />
            </S.FormItemWrapper>
            <S.FormItemWrapper>
              <Toggle
                name="is_activity"
                checked={isActivity}
                onChangeHandler={() => setIsActivity(!isActivity)}
                label="アクティビティ"
              />
            </S.FormItemWrapper>
            <S.FormItemWrapper>
              <Toggle
                name="is_email"
                checked={isEmail}
                onChangeHandler={() => setIsEmail(!isEmail)}
                label="メール"
              />
            </S.FormItemWrapper>
            <S.FormItemWrapper>
              <Toggle
                name="is_mobile_push"
                checked={isMobilePush}
                onChangeHandler={() => setIsMobilePush(!isMobilePush)}
                label="プッシュ"
              />
            </S.FormItemWrapper>
            <S.FormItemWrapper>
              <InputText
                required={false}
                name="short_limit"
                defaultValue={String(notificationType.short_limit || 0)}
                label="短時間送信制限"
              />
            </S.FormItemWrapper>
            <div className="Buttons">
              <Button>
                <a href="/agent/guide_notification_types">
                  <SvgIcon
                    name="keyboard_arrow_left"
                    className="icon"
                    ariaHidden={true}
                    role="presentation"
                  />
                  {I18n.t('generic.back_to_index')}
                </a>
              </Button>
              <Button primary={true}>{I18n.t('generic.update')}</Button>
            </div>
          </Form>
        </Panel>
      </Wrapper>
    </SidebarLayout>
  )
}

const S: { [key: string]: AnyStyledComponent } = {}

S.FormItemWrapper = styled.div`
  margin-top: 24px;
  .note {
    margin-left: 240px;
    font-size: 14px;
    margin-top: 5px;
    color: #888;
    @media (max-width: ${BREAKPOINT_TABLET_SMALL}px) {
      margin-left: 0px;
    }
  }
`

const Wrapper = styled.div`
  width: 100%;
  padding: 22px 40px 80px 46px;

  .Panel {
    max-width: none;
  }

  .Buttons {
    position: relative;
    display: flex;
    justify-content: center;
    margin-top: 48px;

    .Button[type='submit'] {
      width: 160px;
    }

    .Button[type='button'] {
      position: absolute;
      left: 0;
      top: 0;
      display: flex;
      align-items: center;

      .icon {
        margin-left: -10px;
      }
    }
  }

  .InputText + .InputText,
  .InputTextArea + .InputTextArea,
  .InputText + .InputTextArea,
  .InputTextArea + .InputText,
  .InputText + .CheckBox,
  .CheckBox + .CheckBox {
    margin-top: 24px;
  }

  @media (max-width: ${constants.BREAKPOINT_DESKTOP}px) {
    padding: 16px 12px;
  }
`

export default AgentGuideNotificationTypeEdit
